import { useCallback, useRef, useState, type ReactNode } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  EntityCatalogGraphCard,
  type EntityRelationsGraphProps,
} from '@backstage/plugin-catalog-graph';
import { C4_VIEW_MODES, type C4ViewMode } from '../lib/viewModes';
import { exportFileName, findExportableSvg } from '../lib/exportSvg';
import { C4ViewModeSelector } from './C4ViewModeSelector';
import { C4ComponentPanel } from './C4ComponentPanel';
import { C4ExportButtons } from './C4ExportButtons';

const useStyles = makeStyles(theme => ({
  graphWrapper: {
    display: 'contents',
  },
  selectorRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: theme.spacing(1),
  },
  exportRow: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

export type C4EntityCatalogGraphCardProps =
  Partial<EntityRelationsGraphProps> & {
    height?: number;
    title?: string;
    action?: ReactNode;
    defaultMode?: C4ViewMode;
  };

export function C4EntityCatalogGraphCard({
  defaultMode = 'graph',
  action,
  ...props
}: C4EntityCatalogGraphCardProps) {
  const classes = useStyles();
  const [mode, setMode] = useState<C4ViewMode>(defaultMode);
  const { entity } = useEntity();
  const graphRef = useRef<HTMLDivElement | null>(null);

  const getSvg = useCallback(() => findExportableSvg(graphRef.current), []);

  const definition = C4_VIEW_MODES[mode];

  const selectorRow = action ? null : (
    <Box className={classes.selectorRow}>
      <C4ViewModeSelector value={mode} onChange={setMode} size="small" />
    </Box>
  );

  if (definition.kind === 'panel') {
    return (
      <Box>
        {selectorRow}
        <InfoCard title={props.title ?? 'Relações'} action={action}>
          <C4ComponentPanel entity={entity} />
        </InfoCard>
      </Box>
    );
  }

  return (
    <Box>
      {selectorRow}
      <div ref={graphRef} className={classes.graphWrapper}>
        <EntityCatalogGraphCard
          key={mode}
          {...definition.props}
          {...props}
          action={action}
        />
      </div>
      <Box className={classes.exportRow}>
        <C4ExportButtons
          getSvg={getSvg}
          fileName={exportFileName('c4', definition.id, entity.metadata?.name)}
        />
      </Box>
    </Box>
  );
}
